import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

interface Client {
  id: number;
  name: string;
  email: string;
  phone?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface Shipment {
  id: string;
  reference: string;
  status: string;
  destinationType: string;
  createdAt: string;
  itemCount: number;
  skuCount: number;
}

interface MiddleMileRecord {
  id: string;
  outbound_ids: string[];
  fba_ids: string[];
  pattern_middle_mile_id?: string | null;
  pattern_order_id?: string | null;
  warehouse: string;
  quote_amount?: number | null;
  currency: string;
  status: string;
  last_error?: string | null;
  created_at: string;
}

interface StaffDashboardProps {
  token: string;
  onLogout: () => void;
}

const StaffDashboard: React.FC<StaffDashboardProps> = ({ token, onLogout }) => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [shipmentSearchTerm, setShipmentSearchTerm] = useState(searchParams.get('search') || '');
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingShipments, setLoadingShipments] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedShipmentIds, setSelectedShipmentIds] = useState<Set<string>>(new Set());
  const [middleMileRecords, setMiddleMileRecords] = useState<MiddleMileRecord[]>([]);
  const [activeMiddleMile, setActiveMiddleMile] = useState<MiddleMileRecord | null>(null);
  const [middleMileBusy, setMiddleMileBusy] = useState(false);

  // Load all clients on component mount
  useEffect(() => {
    loadClients();
  }, []);

  // Handle URL params for client selection
  useEffect(() => {
    if (clientId && clients.length > 0) {
      const client = clients.find(c => c.id.toString() === clientId);
      if (client && (!selectedClient || selectedClient.id.toString() !== clientId)) {
        setSelectedClient(client);
        loadClientShipments(client.id);
      }
    } else if (!clientId && selectedClient) {
      setSelectedClient(null);
      setShipments([]);
      setSelectedShipmentIds(new Set());
      setMiddleMileRecords([]);
    }
  }, [clientId, clients]);

  // Update search params when shipment search changes
  useEffect(() => {
    if (selectedClient && shipmentSearchTerm) {
      setSearchParams({ search: shipmentSearchTerm });
    } else {
      setSearchParams({});
    }
  }, [shipmentSearchTerm, selectedClient, setSearchParams]);

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      setError(null);
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/staff/clients`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load clients');
      }

      const data = await response.json();
      if (data.success) {
        setClients(data.data);
      } else {
        throw new Error(data.error || 'Failed to load clients');
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      setError(error instanceof Error ? error.message : 'Failed to load clients');
    } finally {
      setLoadingClients(false);
    }
  };

  const loadClientShipments = async (clientId: number) => {
    try {
      setLoadingShipments(true);
      setError(null);
      
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/staff/clients/${clientId}/shipments`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load client shipments');
      }

      const data = await response.json();
      if (data.success) {
        setShipments(data.data);
        setSelectedShipmentIds(new Set());
        loadMiddleMileRecords(clientId);
      } else {
        throw new Error(data.error || 'Failed to load client shipments');
      }
    } catch (error) {
      console.error('Error loading client shipments:', error);
      setError(error instanceof Error ? error.message : 'Failed to load client shipments');
      setShipments([]);
    } finally {
      setLoadingShipments(false);
    }
  };

  const loadMiddleMileRecords = async (clientId: number) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/staff/middle-mile?clientId=${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok && data.success) setMiddleMileRecords(data.data);
    } catch (loadError) {
      console.error('Error loading Middle Mile history:', loadError);
    }
  };

  const createMiddleMileQuote = async () => {
    if (!selectedClient || selectedShipmentIds.size === 0) return;
    const pin = window.prompt('Enter the Middle Mile staff PIN');
    if (!pin) return;
    try {
      setMiddleMileBusy(true);
      setError(null);
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/staff/middle-mile/quote`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId: selectedClient.id, outboundIds: [...selectedShipmentIds], pin })
      });
      const data = await response.json();
      if (data.data) {
        setActiveMiddleMile(data.data);
        await loadMiddleMileRecords(selectedClient.id);
      }
      if (!response.ok || !data.success) throw new Error(data.error || 'Could not create Middle Mile quote');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Could not create Middle Mile quote');
    } finally {
      setMiddleMileBusy(false);
    }
  };

  const retryMiddleMileQuote = async (record: MiddleMileRecord) => {
    if (!selectedClient) return;
    const pin = window.prompt('Enter the Middle Mile staff PIN to try Pattern again');
    if (!pin) return;
    try {
      setMiddleMileBusy(true);
      setError(null);
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/staff/middle-mile/${record.id}/retry-quote`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });
      const data = await response.json();
      if (data.data) setActiveMiddleMile(data.data);
      await loadMiddleMileRecords(selectedClient.id);
      if (!response.ok || !data.success) throw new Error(data.error || 'Pattern still has not synced the FBA ID');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Could not retry Pattern');
    } finally {
      setMiddleMileBusy(false);
    }
  };

  const confirmMiddleMile = async () => {
    if (!selectedClient || !activeMiddleMile) return;
    const pin = window.prompt(`Confirm ${activeMiddleMile.currency} $${Number(activeMiddleMile.quote_amount).toFixed(2)} and enter the staff PIN`);
    if (!pin) return;
    try {
      setMiddleMileBusy(true);
      setError(null);
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/staff/middle-mile/${activeMiddleMile.id}/confirm`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });
      const data = await response.json();
      if (data.data) setActiveMiddleMile(data.data);
      if (!response.ok || !data.success) throw new Error(data.error || 'Could not confirm Middle Mile shipment');
      setSelectedShipmentIds(new Set());
      await loadMiddleMileRecords(selectedClient.id);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Could not confirm Middle Mile shipment');
    } finally {
      setMiddleMileBusy(false);
    }
  };

  const cancelMiddleMile = async (record: MiddleMileRecord) => {
    if (!selectedClient || !window.confirm(`Cancel ${record.pattern_middle_mile_id}? This will cancel it in Pattern.`)) return;
    const pin = window.prompt('Enter the Middle Mile staff PIN to cancel this shipment');
    if (!pin) return;
    try {
      setMiddleMileBusy(true);
      setError(null);
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/staff/middle-mile/${record.id}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });
      const data = await response.json();
      if (data.data) setActiveMiddleMile(data.data);
      await loadMiddleMileRecords(selectedClient.id);
      if (!response.ok || !data.success) throw new Error(data.error || 'Could not cancel the Middle Mile shipment');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Could not cancel the Middle Mile shipment');
    } finally {
      setMiddleMileBusy(false);
    }
  };

  const handleClientSelect = (client: Client) => {
    localStorage.setItem('yyz-cbp-staff-client-id', String(client.id));
    navigate(`/staff/clients/${client.id}`);
  };

  const handleBackToClients = () => {
    navigate('/staff');
    setShipmentSearchTerm('');
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
    String(client.id).includes(clientSearchTerm.trim())
  );

  const filteredShipments = shipments.filter(shipment =>
    shipment.reference.toLowerCase().includes(shipmentSearchTerm.toLowerCase())
  );
  const recentClient = clients.find(client => String(client.id) === localStorage.getItem('yyz-cbp-staff-client-id')) || null;

  const getStatusBadge = (status: string) => {
    const style: React.CSSProperties = {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 'bold'
    };

    switch(status) {
      case 'PENDING':
        return { ...style, backgroundColor: '#fff3cd', color: '#856404' };
      case 'SHIPPED':
        return { ...style, backgroundColor: '#d1ecf1', color: '#0c5460' };
      case 'DELIVERED':
        return { ...style, backgroundColor: '#d4edda', color: '#155724' };
      case 'CANCELLED':
        return { ...style, backgroundColor: '#f8d7da', color: '#721c24' };
      default:
        return { ...style, backgroundColor: '#e2e3e5', color: '#383d41' };
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="header">
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div>
                <h1 style={{ margin: 0, fontSize: '24px' }}>Staff Portal</h1>
                <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>Client Management Dashboard</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <button
                onClick={() => navigate('/staff/manual-invoice')}
                className="button primary"
                style={{ fontSize: '14px' }}
              >
                Manual Invoice Generator
              </button>
              {selectedClient && (
                <button
                  onClick={handleBackToClients}
                  className="button secondary"
                >
                  ← Back to Clients
                </button>
              )}
              <button
                onClick={onLogout}
                className="button secondary"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Error Display */}
        {error && (
          <div className="error" style={{ marginBottom: '20px' }}>
            {error}
          </div>
        )}

        {!selectedClient ? (
          /* Client List View */
          <div>
            <div style={{ marginBottom: '30px' }}>
              <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '20px', color: '#333' }}>
                All Clients
              </h2>
              {recentClient && (
                <button
                  className="button secondary"
                  onClick={() => handleClientSelect(recentClient)}
                  style={{ marginBottom: '14px', fontSize: '13px' }}
                >
                  Continue with {recentClient.name}
                </button>
              )}
              
              {/* Search Bar */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  className="input"
                  placeholder="Search clients by name, email, or ID..."
                  value={clientSearchTerm}
                  onChange={(e) => setClientSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {loadingClients ? (
              <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '18px', color: '#666' }}>Loading clients...</div>
              </div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                      <th style={{ 
                        padding: '15px 20px', 
                        textAlign: 'left', 
                        fontWeight: 'bold', 
                        color: '#495057',
                        fontSize: '14px'
                      }}>
                        CLIENT NAME
                      </th>
                      <th style={{ 
                        padding: '15px 20px', 
                        textAlign: 'left', 
                        fontWeight: 'bold', 
                        color: '#495057',
                        fontSize: '14px'
                      }}>
                        EMAIL
                      </th>
                      <th style={{ 
                        padding: '15px 20px', 
                        textAlign: 'left', 
                        fontWeight: 'bold', 
                        color: '#495057',
                        fontSize: '14px'
                      }}>
                        JOINED
                      </th>
                      <th style={{ 
                        padding: '15px 20px', 
                        textAlign: 'center', 
                        fontWeight: 'bold', 
                        color: '#495057',
                        fontSize: '14px',
                        width: '100px'
                      }}>
                        ACTION
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((client, index) => (
                      <tr 
                        key={client.id}
                        style={{ 
                          borderBottom: '1px solid #dee2e6',
                          backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#e3f2fd';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
                        }}
                        onClick={() => handleClientSelect(client)}
                      >
                        <td style={{ 
                          padding: '15px 20px', 
                          fontSize: '14px', 
                          fontWeight: '500',
                          color: '#333'
                        }}>
                          {client.name}
                        </td>
                        <td style={{ 
                          padding: '15px 20px', 
                          fontSize: '14px',
                          color: '#666'
                        }}>
                          {client.email}
                        </td>
                        <td style={{ 
                          padding: '15px 20px', 
                          fontSize: '14px',
                          color: '#666'
                        }}>
                          {new Date(client.createdAt).toLocaleDateString()}
                        </td>
                        <td style={{ 
                          padding: '15px 20px', 
                          textAlign: 'center'
                        }}>
                          <button
                            className="button"
                            style={{ 
                              fontSize: '12px', 
                              padding: '6px 12px',
                              backgroundColor: '#007bff',
                              border: 'none',
                              borderRadius: '4px',
                              color: 'white',
                              cursor: 'pointer'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClientSelect(client);
                            }}
                          >
                            View Shipments
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {filteredClients.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p style={{ color: '#666' }}>No clients found</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Shipments View for Selected Client */
          <div>
            <div style={{ marginBottom: '30px' }}>
              <div className="card" style={{ marginBottom: '20px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
                  {selectedClient.name}
                </h2>
                <p style={{ color: '#666', margin: 0 }}>{selectedClient.email}</p>
              </div>

              <h3 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '20px', color: '#333' }}>
                Outbound Shipments
              </h3>
              
              {/* Search Bar */}
              <div style={{ maxWidth: '400px' }}>
                <input
                  type="text"
                  className="input"
                  placeholder="Search shipments..."
                  value={shipmentSearchTerm}
                  onChange={(e) => setShipmentSearchTerm(e.target.value)}
                  style={{ maxWidth: '400px' }}
                />
                <button className="button primary" disabled={selectedShipmentIds.size === 0 || middleMileBusy} onClick={createMiddleMileQuote}>
                  {middleMileBusy ? 'Working in Pattern…' : `Create Middle Mile Inbound (${selectedShipmentIds.size})`}
                </button>
              </div>
            </div>

            {activeMiddleMile && (
              <div className="card" style={{ marginBottom: '20px', border: '2px solid #007bff' }}>
                <h3 style={{ marginTop: 0 }}>Middle Mile {activeMiddleMile.status === 'confirmed' ? 'Created' : 'Quote'}</h3>
                <p><strong>Destination:</strong> {activeMiddleMile.warehouse}</p>
                <p><strong>FBA IDs:</strong> {activeMiddleMile.fba_ids.join(', ')}</p>
                {activeMiddleMile.quote_amount != null && <p><strong>Total:</strong> {activeMiddleMile.currency} ${Number(activeMiddleMile.quote_amount).toFixed(2)}</p>}
                {activeMiddleMile.pattern_middle_mile_id && <p><strong>Middle Mile number:</strong> {activeMiddleMile.pattern_middle_mile_id}</p>}
                <p><strong>Status:</strong> {activeMiddleMile.status.replace(/_/g, ' ')}</p>
                {activeMiddleMile.last_error && <p style={{ color: '#856404' }}>{activeMiddleMile.last_error}</p>}
                {activeMiddleMile.status === 'waiting_for_pattern_sync' && (
                  <button className="button secondary" disabled={middleMileBusy} onClick={() => retryMiddleMileQuote(activeMiddleMile)}>
                    Try Again
                  </button>
                )}
                {['quoted', 'quote_changed'].includes(activeMiddleMile.status) && (
                  <button className="button primary" disabled={middleMileBusy} onClick={confirmMiddleMile}>
                    Confirm and Pay
                  </button>
                )}
                {activeMiddleMile.status === 'confirmed' && (
                  <button className="button secondary" disabled={middleMileBusy} onClick={() => cancelMiddleMile(activeMiddleMile)}>
                    Cancel Middle Mile
                  </button>
                )}
              </div>
            )}

            {loadingShipments ? (
              <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '18px', color: '#666' }}>Loading shipments...</div>
              </div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                      <th style={{ padding: '15px 12px', width: '36px' }}>
                        <input type="checkbox" aria-label="Select all visible outbounds"
                          checked={filteredShipments.length > 0 && filteredShipments.every(s => selectedShipmentIds.has(s.id))}
                          onChange={(e) => setSelectedShipmentIds(current => {
                            const next = new Set(current);
                            filteredShipments.forEach(s => e.target.checked ? next.add(s.id) : next.delete(s.id));
                            return next;
                          })} />
                      </th>
                      <th style={{ 
                        padding: '15px 20px', 
                        textAlign: 'left', 
                        fontWeight: 'bold', 
                        color: '#495057',
                        fontSize: '14px'
                      }}>
                        SHIPMENT REFERENCE
                      </th>
                      <th style={{ 
                        padding: '15px 20px', 
                        textAlign: 'left', 
                        fontWeight: 'bold', 
                        color: '#495057',
                        fontSize: '14px'
                      }}>
                        STATUS
                      </th>
                      <th style={{ 
                        padding: '15px 20px', 
                        textAlign: 'left', 
                        fontWeight: 'bold', 
                        color: '#495057',
                        fontSize: '14px'
                      }}>
                        ITEMS
                      </th>
                      <th style={{ 
                        padding: '15px 20px', 
                        textAlign: 'left', 
                        fontWeight: 'bold', 
                        color: '#495057',
                        fontSize: '14px'
                      }}>
                        CREATED
                      </th>
                      <th style={{ 
                        padding: '15px 20px', 
                        textAlign: 'center', 
                        fontWeight: 'bold', 
                        color: '#495057',
                        fontSize: '14px',
                        width: '120px'
                      }}>
                        ACTION
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredShipments.map((shipment, index) => (
                      <tr 
                        key={shipment.id}
                        style={{ 
                          borderBottom: '1px solid #dee2e6',
                          backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#e3f2fd';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
                        }}
                        onClick={() => navigate(`/staff/clients/${selectedClient.id}/shipments/${shipment.id}`)}
                      >
                        <td style={{ padding: '15px 12px' }} onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" aria-label={`Select ${shipment.reference}`} checked={selectedShipmentIds.has(shipment.id)}
                            onChange={(e) => setSelectedShipmentIds(current => {
                              const next = new Set(current);
                              e.target.checked ? next.add(shipment.id) : next.delete(shipment.id);
                              return next;
                            })} />
                        </td>
                        <td style={{ 
                          padding: '15px 20px', 
                          fontSize: '14px', 
                          fontWeight: '500',
                          color: '#333'
                        }}>
                          {shipment.reference}
                        </td>
                        <td style={{ 
                          padding: '15px 20px', 
                          fontSize: '14px'
                        }}>
                          <span style={getStatusBadge(shipment.status)}>
                            {shipment.status}
                          </span>
                        </td>
                        <td style={{ 
                          padding: '15px 20px', 
                          fontSize: '14px',
                          color: '#666'
                        }}>
                          {shipment.itemCount} items, {shipment.skuCount} SKUs
                        </td>
                        <td style={{ 
                          padding: '15px 20px', 
                          fontSize: '14px',
                          color: '#666'
                        }}>
                          {new Date(shipment.createdAt).toLocaleDateString()}
                        </td>
                        <td style={{ 
                          padding: '15px 20px', 
                          textAlign: 'center'
                        }}>
                          <button
                            className="button"
                            style={{ 
                              fontSize: '12px', 
                              padding: '6px 12px',
                              backgroundColor: '#007bff',
                              border: 'none',
                              borderRadius: '4px',
                              color: 'white',
                              cursor: 'pointer'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/staff/clients/${selectedClient.id}/shipments/${shipment.id}`);
                            }}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {filteredShipments.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p style={{ color: '#666' }}>No shipments found</p>
                  </div>
                )}
              </div>
            )}

            {middleMileRecords.length > 0 && (
              <div className="card" style={{ marginTop: '20px' }}>
                <h3 style={{ marginTop: 0 }}>Middle Mile History</h3>
                {middleMileRecords.map(record => (
                  <div key={record.id} style={{ padding: '10px 0', borderTop: '1px solid #eee' }}>
                    <strong>{record.pattern_middle_mile_id || 'Pending number'}</strong> — {record.status.replace(/_/g, ' ')}
                    {record.quote_amount != null && ` — ${record.currency} $${Number(record.quote_amount).toFixed(2)}`}
                    <div style={{ color: '#666', fontSize: '13px' }}>{record.fba_ids.join(', ')}</div>
                    {record.status === 'waiting_for_pattern_sync' && (
                      <button className="button secondary" style={{ marginTop: '6px', fontSize: '12px' }} disabled={middleMileBusy} onClick={() => retryMiddleMileQuote(record)}>
                        Try Again
                      </button>
                    )}
                    {record.status === 'confirmed' && (
                      <button className="button secondary" style={{ marginTop: '6px', fontSize: '12px' }} disabled={middleMileBusy} onClick={() => cancelMiddleMile(record)}>
                        Cancel Middle Mile
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

export default StaffDashboard;
